import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2, Send, MessageSquare } from "lucide-react";
import { fmtDate } from "@/lib/labels";
import { toast } from "sonner";

export const RECURRENCE_LABELS = {
  ukentlig: "Ukentlig",
  hver_14_dag: "Hver 14. dag",
  manedlig: "Månedlig",
  kvartalsvis: "Kvartalsvis",
  arlig: "Årlig",
};

const RECURRENCE_OPTS = [["ingen", "Gjentas ikke"], ...Object.entries(RECURRENCE_LABELS)];

const DEFAULTS = { title: "", description: "", customer_id: "none", due_date: "", priority: "normal", assignee_id: "self", recurrence: "ingen", tagged: [] };

export default function TaskFormDialog({ open, onOpenChange, task, lockedCustomer, onSaved }) {
  const isEdit = !!task;
  const { user } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [users, setUsers] = useState([]);
  const [f, setF] = useState(DEFAULTS);
  const [busy, setBusy] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [sending, setSending] = useState(false);

  const loadComments = (id) => api.get(`/tasks/${id}/comments`).then((r) => setComments(r.data)).catch(() => {});

  useEffect(() => {
    if (!open) return;
    if (!lockedCustomer) api.get("/customers").then((r) => setCustomers([...r.data].sort((a, b) => (a.legal_name || "").localeCompare(b.legal_name || "", "nb")))).catch(() => {});
    api.get("/auth/users").then((r) => setUsers(r.data)).catch(() => {});
    setF({
      title: task?.title || "",
      description: task?.description || "",
      customer_id: lockedCustomer ? lockedCustomer.id : (task?.customer_id || "none"),
      due_date: task?.due_date || "",
      priority: task?.priority || "normal",
      assignee_id: task?.assignee_id || "self",
      recurrence: task?.recurrence || "ingen",
      tagged: task?.tagged_user_ids || [],
    });
    setNewComment("");
    if (task?.id) loadComments(task.id); else setComments([]);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleTag = (id) => setF((p) => ({ ...p, tagged: p.tagged.includes(id) ? p.tagged.filter((x) => x !== id) : [...p.tagged, id] }));

  const sendComment = async () => {
    const text = newComment.trim();
    if (!text || !task?.id) return;
    setSending(true);
    try {
      await api.post(`/tasks/${task.id}/comments`, { text });
      setNewComment("");
      loadComments(task.id);
    } catch (e) { toast.error(apiError(e)); } finally { setSending(false); }
  };

  const submit = async () => {
    if (f.title.trim().length < 2) { toast.error("Skriv en tittel"); return; }
    setBusy(true);
    try {
      const cust = lockedCustomer || customers.find((c) => c.id === f.customer_id);
      const asg = f.assignee_id === "self" ? { id: user?.id, name: user?.name } : users.find((u) => u.id === f.assignee_id);
      const tagged_names = f.tagged.map((id) => users.find((u) => u.id === id)?.name).filter(Boolean);
      const payload = {
        title: f.title.trim(),
        description: f.description.trim(),
        customer_id: lockedCustomer ? lockedCustomer.id : (f.customer_id === "none" ? null : f.customer_id),
        customer_name: lockedCustomer ? lockedCustomer.legal_name : (f.customer_id === "none" ? null : (cust?.legal_name || null)),
        due_date: f.due_date || null,
        priority: f.priority,
        recurrence: f.recurrence === "ingen" ? null : f.recurrence,
        category: task?.category || "oppfolging",
        tagged_user_ids: f.tagged,
        tagged_names,
      };
      if (asg?.id) { payload.assignee_id = asg.id; payload.assignee_name = asg.name; }
      if (isEdit) await api.put(`/tasks/${task.id}`, payload);
      else await api.post("/tasks", payload);
      toast.success(isEdit ? "Oppgave oppdatert" : "Oppgave opprettet");
      onOpenChange(false);
      onSaved?.();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/tasks/${task.id}`);
      toast.success("Oppgave slettet");
      onOpenChange(false);
      onSaved?.();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-popover border-border max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Rediger oppgave" : "Ny oppgave"}</DialogTitle>
          <DialogDescription>
            {lockedCustomer ? `Knyttet til ${lockedCustomer.legal_name}. Sett gjerne frist og gjentakelse.` : "Knytt den gjerne til en kunde og sett en frist."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="label-caps">Tittel</Label>
            <Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="mt-1 bg-secondary border-border" data-testid="task-title" placeholder="Hva skal gjøres?" />
          </div>
          <div>
            <Label className="label-caps">Beskrivelse (valgfritt)</Label>
            <Textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="mt-1 bg-secondary border-border" data-testid="task-description" rows={3} placeholder="Detaljer …" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-caps">Frist</Label>
              <Input type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} className="mt-1 bg-secondary border-border" data-testid="task-due" />
            </div>
            <div>
              <Label className="label-caps">Prioritet</Label>
              <Select value={f.priority} onValueChange={(v) => setF({ ...f, priority: v })}>
                <SelectTrigger className="mt-1 bg-secondary border-border" data-testid="task-priority"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-popover border-border">
                  <SelectItem value="lav">Lav</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="hoy">Høy</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="label-caps">Gjentakelse</Label>
            <Select value={f.recurrence} onValueChange={(v) => setF({ ...f, recurrence: v })}>
              <SelectTrigger className="mt-1 bg-secondary border-border" data-testid="task-recurrence"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-popover border-border">
                {RECURRENCE_OPTS.map(([k, label]) => <SelectItem key={k} value={k}>{label}</SelectItem>)}
              </SelectContent>
            </Select>
            {f.recurrence !== "ingen" && <p className="text-[11px] text-muted-foreground mt-1">Når oppgaven markeres ferdig, opprettes automatisk neste forekomst.</p>}
          </div>
          {!lockedCustomer && (
            <div>
              <Label className="label-caps">Kunde (valgfritt)</Label>
              <Select value={f.customer_id} onValueChange={(v) => setF({ ...f, customer_id: v })}>
                <SelectTrigger className="mt-1 bg-secondary border-border" data-testid="task-customer"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-popover border-border max-h-64">
                  <SelectItem value="none">Ingen kunde</SelectItem>
                  {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.legal_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="label-caps">Ansvarlig</Label>
            <Select value={f.assignee_id} onValueChange={(v) => setF({ ...f, assignee_id: v })}>
              <SelectTrigger className="mt-1 bg-secondary border-border" data-testid="task-assignee"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-popover border-border max-h-64">
                <SelectItem value="self">Meg selv</SelectItem>
                {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-caps">Tagg kolleger</Label>
            <div className="flex flex-wrap gap-2 mt-1" data-testid="task-tags">
              {users.map((u) => {
                const on = f.tagged.includes(u.id);
                return (
                  <button type="button" key={u.id} onClick={() => toggleTag(u.id)} data-testid={`task-tag-${u.id}`}
                    className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${on ? "bg-blue-500/20 border-blue-500/50 text-blue-300" : "bg-secondary border-border text-muted-foreground hover:text-foreground"}`}>
                    @{u.name}
                  </button>
                );
              })}
              {users.length === 0 && <span className="text-xs text-muted-foreground">Ingen andre brukere</span>}
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">Taggede kolleger får varsel og ser oppgaven under «Tagget meg».</p>
          </div>
          {isEdit && (
            <div className="pt-1 border-t border-border">
              <Label className="label-caps flex items-center gap-1 mt-3"><MessageSquare className="w-3.5 h-3.5" /> Kommentarer / svar</Label>
              <div className="mt-2 space-y-2 max-h-48 overflow-y-auto" data-testid="task-comments">
                {comments.length === 0 && <p className="text-xs text-muted-foreground">Ingen kommentarer ennå.</p>}
                {comments.map((c) => (
                  <div key={c.id} className="rounded-md bg-secondary border border-border p-2.5" data-testid={`task-comment-${c.id}`}>
                    <p className="text-xs"><span className="font-medium">{c.user_name}</span> <span className="text-muted-foreground">· {fmtDate(c.created_at)}</span></p>
                    <p className="text-sm mt-0.5 whitespace-pre-wrap">{c.text}</p>
                  </div>
                ))}
              </div>
              <div className="flex gap-2 mt-2">
                <Input value={newComment} onChange={(e) => setNewComment(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); sendComment(); } }}
                  className="bg-secondary border-border" placeholder="Skriv et svar …" data-testid="task-comment-input" />
                <Button type="button" onClick={sendComment} disabled={sending || !newComment.trim()} data-testid="task-comment-send" className="bg-white text-black hover:bg-white/90 shrink-0"><Send className="w-4 h-4" /></Button>
              </div>
            </div>
          )}
        </div>
        <DialogFooter className="gap-2 sm:justify-between">
          {isEdit ? (
            <Button variant="ghost" onClick={remove} disabled={busy} data-testid="task-delete" className="text-destructive hover:text-destructive hover:bg-destructive/10"><Trash2 className="w-4 h-4 mr-1" /> Slett</Button>
          ) : <span />}
          <Button onClick={submit} disabled={busy} data-testid="task-save" className="bg-white text-black hover:bg-white/90">{busy ? "Lagrer…" : (isEdit ? "Lagre endringer" : "Opprett oppgave")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
